import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import EditChangeSummary from "./EditChangeSummary";
import { PHONE_COUNTRIES, getCountryByCode } from "../helpers/phoneHelper";
import {
  BR_PHONE_MAX_DIGITS,
  EMAIL_MAX_LENGTH,
  buildContactPhone,
  extractServerMessage,
  formatBrazilianPhone,
  formatPhoneForDisplay,
  isValidContactEmail,
  isValidContactPhone,
  normalizeEmail,
  normalizePhoneDigits,
} from "../helpers/contactEditHelper";
import "../styles/AddressEditModal.css";

const MAX_INTERNATIONAL_DIGITS = 15;

const displayOrDash = (value) => (value == null || String(value).trim() === "" ? "-" : String(value));

const fieldError = (filled, valid, touched, invalidKey) => (filled && touched && !valid ? invalidKey : null);

// Blank fields mean "unchanged". onSave receives only the filled fields: { email?, phone? }.
const ContactEditModal = ({ isOpen, currentEmail, currentPhone, onClose, onSave, t }) => {
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("BR");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [touched, setTouched] = useState({ email: false, phone: false });
  const [step, setStep] = useState("form");
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const emailRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setEmail("");
    setCountryCode("BR");
    setPhoneDigits("");
    setTouched({ email: false, phone: false });
    setStep("form");
    setSaving(false);
    setServerError(null);
    setTimeout(() => emailRef.current?.focus(), 0);
  }, [isOpen]);

  const isBrazil = countryCode === "BR";
  const emailFilled = email.trim().length > 0;
  const phoneFilled = phoneDigits.length > 0;
  const emailValid = isValidContactEmail(email);
  const phoneValid = isValidContactPhone(countryCode, phoneDigits);
  const canSave = (emailFilled || phoneFilled) && (!emailFilled || emailValid) && (!phoneFilled || phoneValid);

  const changes = {};
  if (emailFilled) changes.email = normalizeEmail(email);
  if (phoneFilled) changes.phone = buildContactPhone(countryCode, phoneDigits);

  const isHidden = (value) => !value || (typeof value === "string" && value.includes("*"));
  const showHint = isHidden(currentEmail) || isHidden(currentPhone);

  const emailErrorKey = fieldError(emailFilled, emailValid, touched.email, "emailInvalid");
  const phoneErrorKey = fieldError(phoneFilled, phoneValid, touched.phone, isBrazil ? "phoneInvalidBr" : "phoneInvalid");
  const emailError = emailErrorKey ? t(`clientDetails.contact.errors.${emailErrorKey}`) : null;
  const phoneError = phoneErrorKey ? t(`clientDetails.contact.errors.${phoneErrorKey}`) : null;

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched({ email: true, phone: true });
    if (!canSave) return;
    setServerError(null);
    setStep("confirm");
  };

  const handleConfirm = async () => {
    setSaving(true);
    setServerError(null);
    try {
      await onSave(changes);
    } catch (error) {
      const { status, message } = extractServerMessage(error);
      setServerError(
        message
          || (status === 403 ? t("clientDetails.contact.errorForbidden") : t("clientDetails.contact.errorGeneric")),
      );
      setStep("form");
    } finally {
      setSaving(false);
    }
  };

  const handlePhoneChange = (event) => {
    setPhoneDigits(normalizePhoneDigits(event.target.value, isBrazil ? BR_PHONE_MAX_DIGITS : MAX_INTERNATIONAL_DIGITS));
  };

  const handleCountryChange = (event) => {
    const next = event.target.value;
    setCountryCode(next);
    setPhoneDigits((prev) => normalizePhoneDigits(prev, next === "BR" ? BR_PHONE_MAX_DIGITS : MAX_INTERNATIONAL_DIGITS));
  };

  const inputClass = (hasError) => `portal-input address-edit-input${hasError ? " input-error" : ""}`;

  const summaryRows = [];
  if (changes.email) {
    summaryRows.push({
      key: "email",
      label: t("clientDetails.contact.fields.email"),
      before: displayOrDash(currentEmail),
      after: changes.email,
    });
  }
  if (changes.phone) {
    summaryRows.push({
      key: "phone",
      label: t("clientDetails.contact.fields.phone"),
      before: displayOrDash(formatPhoneForDisplay(currentPhone)),
      after: formatPhoneForDisplay(changes.phone),
    });
  }

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose}>
      <div className="address-edit-modal contact-edit-modal" data-testid="contact-edit-modal">
        <h3 className="address-edit-title">
          {t(step === "confirm" ? "clientDetails.contact.confirmTitle" : "clientDetails.contact.modalTitle")}
        </h3>

        {serverError && (
          <div className="address-edit-banner is-error" role="alert">{serverError}</div>
        )}

        {step === "form" && (
          <form className="address-edit-form contact-edit-form" onSubmit={handleSubmit} noValidate>
            <p className="address-edit-hint address-edit-wide">
              {showHint ? `${t("clientDetails.contact.hiddenHint")} ` : ""}
              {t("clientDetails.contact.blankHint")}
            </p>

            <div className="address-edit-field address-edit-wide">
              <label htmlFor="contact-email">{t("clientDetails.contact.fields.email")}</label>
              <input
                id="contact-email"
                ref={emailRef}
                type="email"
                autoComplete="email"
                className={inputClass(emailError)}
                value={email}
                maxLength={EMAIL_MAX_LENGTH}
                onChange={(event) => setEmail(event.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                placeholder="nome@exemplo.com"
                aria-invalid={Boolean(emailError)}
                aria-describedby={emailError ? "contact-email-error" : undefined}
                disabled={saving}
              />
              {emailError && (
                <span className="address-edit-field-error" id="contact-email-error" role="alert">{emailError}</span>
              )}
            </div>

            <div className="address-edit-field address-edit-wide">
              <label htmlFor="contact-phone">{t("clientDetails.contact.fields.phone")}</label>
              <div className="contact-edit-phone-control">
                <select
                  className="portal-select address-edit-input address-edit-select"
                  value={countryCode}
                  onChange={handleCountryChange}
                  disabled={saving}
                  aria-label={t("clientDetails.contact.fields.country")}
                >
                  {PHONE_COUNTRIES.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.flag} {country.dialCode}
                    </option>
                  ))}
                </select>
                <input
                  id="contact-phone"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  className={inputClass(phoneError)}
                  value={isBrazil ? formatBrazilianPhone(phoneDigits) : phoneDigits}
                  onChange={handlePhoneChange}
                  onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
                  placeholder={isBrazil ? "(00) 00000-0000" : getCountryByCode(countryCode).example}
                  aria-invalid={Boolean(phoneError)}
                  aria-describedby={phoneError ? "contact-phone-error" : undefined}
                  disabled={saving}
                />
              </div>
              {phoneError && (
                <span className="address-edit-field-error" id="contact-phone-error" role="alert">{phoneError}</span>
              )}
            </div>

            <div className="address-edit-actions address-edit-wide">
              <button type="button" className="portal-secondary-button" onClick={onClose}>
                {t("clientDetails.address.cancel")}
              </button>
              <button type="submit" className="portal-button" disabled={!canSave}>
                {t("clientDetails.address.save")}
              </button>
            </div>
          </form>
        )}

        {step === "confirm" && (
          <div className="address-edit-confirm">
            <EditChangeSummary
              beforeLabel={t("clientDetails.address.before")}
              afterLabel={t("clientDetails.address.after")}
              rows={summaryRows}
            />
            <div className="address-edit-actions">
              <button type="button" className="portal-secondary-button" onClick={() => setStep("form")} disabled={saving}>
                {t("clientDetails.address.back")}
              </button>
              <button type="button" className="portal-button" onClick={handleConfirm} disabled={saving}>
                {saving ? t("clientDetails.address.saving") : t("clientDetails.address.confirm")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ContactEditModal;
